import React, {useState} from 'react';
import {StorageObject} from '../../../models/StorageObject';
import {Valid} from '../../validation/Valid';
import {Accordion} from '../../Accordion/Accordion';
import {ContentWrapper, TextInputLong} from './FileMetaDataTabMain.style';
import {t} from 'i18next';
import {LabelView} from '../../LabelView/LabelView';
import {Label} from '../../../models/Label';
import {addAllLabelEntryMap, getLabelEntryMapInitialized, getLabelMapKeys} from '../../../utils/StorageObjectUtils';
import {
    FileMetaDataDialog_changeComment,
    FileMetaDataDialog_changeLabels,
    FileMetaDataDialog_changeLocales,
    FileMetaDataDialog_changeVersion,
    FileMetaDataTabLocales_changeLocales,
    FileMetaDataTabMain_changeLabels,
    LabelView_addAllLocales
} from '../../../init/events';
import cloneDeep from 'lodash/cloneDeep';
import EventEmitter from 'events';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {FileBasicData} from './FileBasicData';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {isMandatoryComponent, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_COMMENT,
    STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';
import {isValidText} from '../../../utils';

export interface FileMetaDataTabMainProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly item: StorageObject;
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly labelsInitialState: Map<string, Label>;
    readonly versionInitialState: string;
    readonly eventEmitter: EventEmitter;
}

/**
 * @name FileMetaDataTabMain
 * @description
 * The dialog to add the metadata of a newly created file or edited file - Metadata Tabs
 */
export const FileMetaDataTabMain: React.VFC<FileMetaDataTabMainProps> = (props) => {
    const [labelsInput, setLabelsInput] = useState<Map<string, Label>>(getLabelEntryMapInitialized(props.defaultLocale, props.item.name, props.item.labels));
    const [versionInput, setVersionInput] = useState(props.item.metadata.version ?? '');
    const [commentInput, setCommentInput] = useState(props.item.metadata.comment ?? '');

    const handleAddAllLocales = () => {
        let locales = addAllLabelEntryMap(props.defaultLocale, props.parentLocales, labelsInput);
        props.eventEmitter.emit(LabelView_addAllLocales, locales);
    };

    const handleResetLocales = () => {
        handleChangeLabels(cloneDeep(props.labelsInitialState));
    };

    const handleChangeLabelsState = (labels: Map<string, Label>) => {
        setLabelsInput(labels);
    };

    const handleChangeLabels = (labels: Map<string, Label>) => {
        setLabelsInput(labels);
        let locales = getLabelMapKeys(props.defaultLocale, labels);
        props.eventEmitter.emit(FileMetaDataTabLocales_changeLocales, locales);
        props.eventEmitter.emit(FileMetaDataDialog_changeLocales, locales);
        props.eventEmitter.emit(FileMetaDataDialog_changeLabels, labels);
    };

    const handleResetLabelsComponent = () => {
        handleResetLocales();
        let version = cloneDeep(props.versionInitialState);
        setVersionInput(version);
        props.eventEmitter.emit(FileMetaDataDialog_changeVersion, version);
    };

    const handleChangeVersion = (version: string) => {
        setVersionInput(version);
        props.eventEmitter.emit(FileMetaDataDialog_changeVersion, version);
    };

    const handleCommentInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        setCommentInput(eventValue);
        props.eventEmitter.emit(FileMetaDataDialog_changeComment, eventValue);
    };

    props.eventEmitter.on(FileMetaDataTabMain_changeLabels, handleChangeLabelsState);

    return (
        <ContentWrapper id={generateIdentifier()}>
            {(isVisibleComponent(STORAGE_OBJECT_NAME, props.templatePropertySettings)
                    || isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, props.templatePropertySettings)
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, props.templatePropertySettings)))) &&
                <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_BASIC_INFORMATION')}>
                    <FileBasicData templatePropertySettings={props.templatePropertySettings}
                                   language={props.language}
                                   item={props.item}
                    />
                </Accordion>
            }
            {((isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings)))
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings))) &&
                <Accordion accordionHeadline={t('LOCALE_LABELS_CAPTION')}
                           startOpen>
                    <LabelView templatePropertySettings={props.templatePropertySettings}
                               defaultLocale={props.defaultLocale}
                               labels={labelsInput}
                               version={versionInput}
                               parentLocales={props.parentLocales}
                               eventEmitter={props.eventEmitter}
                               onLabelsChange={handleChangeLabels}
                               onVersionChange={handleChangeVersion}
                               onAddAllLabels={handleAddAllLocales}
                               onReset={handleResetLabelsComponent}/>
                </Accordion>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, props.templatePropertySettings)) &&
                <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_COMMENT')}
                           accordionSubHeadline={' ' + t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_COMMENT_INFO')}
                           startOpen>
                    <Valid
                        isValid={(isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, props.templatePropertySettings) ? isValidText(commentInput) : true)}
                        message={t('META_DATA_DIALOG_COMMENT_NOT_VALID_CONSTRAINT')}>
                        <TextInputLong id={generateIdentifier('comment')}
                                       label={t('UPLOAD_FILE_META_DATA_DIALOG_COMMENT_LABEL')}
                                       required
                                       value={commentInput}
                                       onChange={handleCommentInput}/>
                    </Valid>
                </Accordion>
            }
        </ContentWrapper>
    );
}